import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { useEffect, useRef } from 'react'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { fetchYakurekiWithDelta } from '@/shared/apis/yakurekiSync'
import {
  executeSuperReload,
  shouldExecuteSuperReload,
} from '@/shared/helpers/superReload'
import { Yakureki } from '@/shared/types/yakurekiCore'
import { GlobalState } from '@/stores/reducer'
import { useSettingQuery } from '@/features/auth'

interface UsePatientListQueryOptions {
  enabled?: boolean
  pollingEnabled?: boolean
}

/**
 * 患者リスト取得クエリ
 *
 * 重要な設計判断:
 * - 差分同期（Delta Sync）ロジックを維持
 * - localStorage を併用してタイムスタンプ管理
 * - 2秒ポーリングでリアルタイム同期
 *
 * @param options.enabled - クエリを有効にするか（ログイン状態など）
 * @param options.pollingEnabled - 2秒ポーリングを有効にするか
 */
export const usePatientListQuery = (
  options: UsePatientListQueryOptions = {}
) => {
  const { enabled = true, pollingEnabled = true } = options
  const isLoggedIn = useSelector((state: GlobalState) => state.isLoggedIn)
  const { data: setting } = useSettingQuery()
  // 表示日数は ui_config (UserUIConfig) 管理。
  const displayDays = setting?.ui_config?.display_days ?? 3
  const queryClient = useQueryClient()

  const query = useQuery({
    // displayDays を queryKey に含める（@tanstack/query/exhaustive-deps 対応）
    queryKey: [...queryKeys.patients.list(), displayDays],
    queryFn: async (): Promise<Yakureki[]> => {
      // スーパーリロードのチェック（1日に1回全件取得）
      if (shouldExecuteSuperReload()) {
        return executeSuperReload()
      }
      // 既存の差分同期ロジックをそのまま使用
      // localStorage でタイムスタンプ管理、差分のみ取得してマージ
      return fetchYakurekiWithDelta(displayDays)
    },
    enabled: enabled && isLoggedIn,
    staleTime: 0, // リアルタイム同期のため常に stale
    refetchInterval: pollingEnabled ? 2000 : false,
    refetchIntervalInBackground: false, // バックグラウンドでは停止（タブ復帰時に即refetch）
  })

  // 前回のリストを保持（status変化検知用）
  const prevListRef = useRef<Map<string, string>>(new Map())

  // リストのstatus変化を監視し、terminal status (completed/copied/failed*) に遷移した
  // 患者の SOAP 関連キャッシュを更新する。
  //
  // legacy: pending → processing → completed/failed の流れで processing を必ず経由
  // streaming: Pass 1 SOAP 生成が 2 秒 polling を跨がないと recording → completed/failed
  // 直行となる場合があるため、prevStatus を processing に限定せず「terminal に到達した」
  // ことを検知する。これで streaming でも patientDetail が確実に invalidate される。
  useEffect(() => {
    if (!query.data) return

    const currentList = query.data
    const prevStatusMap = prevListRef.current

    const isTerminal = (s: string): boolean =>
      s === 'completed' ||
      s === 'copied' ||
      s === 'failed' ||
      s === 'failed_short'

    currentList.forEach((patient) => {
      const prevStatus = prevStatusMap.get(patient.id)
      // 非 terminal → terminal への遷移を検知し、SOAP関連キャッシュを更新
      if (
        prevStatus !== undefined &&
        prevStatus !== patient.status &&
        !isTerminal(prevStatus) &&
        isTerminal(patient.status)
      ) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.yakureki.detail(patient.id),
        })
        // removeQueries で dataUpdateCount をリセットし、ポーリングを再開させる
        // invalidateQueries だと累積カウントが残りポーリングが再開しない
        queryClient.removeQueries({
          queryKey: queryKeys.soapImprovement.detail(patient.id),
        })
      }
    })

    // 現在のstatusを保存
    const newStatusMap = new Map<string, string>()
    currentList.forEach((patient) => {
      newStatusMap.set(patient.id, patient.status)
    })
    prevListRef.current = newStatusMap
  }, [query.data, queryClient])

  /**
   * 手動リロード（スーパーリロード）
   * localStorage をクリアして全件取得
   */
  const reloadPatientList = async (): Promise<void> => {
    await executeSuperReload()
    // キャッシュを無効化して再取得
    await queryClient.invalidateQueries({
      queryKey: queryKeys.patients.list(),
    })
  }

  return {
    reloadPatientList,
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  }
}
